import React, { useState } from 'react';
import { FileText, Settings, Calculator, Edit2, Save, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { formatAttributeValue, getChoiceOptions, formatNumber } from './utils';
import CustomAttributesConfig from './CustomAttributesConfig';
import { evaluateFormula, calculateTotals } from './formulaEvaluator';

const CustomAttributesDisplay = ({
  fields = [],
  attributes = {},
  title = 'Additional Data',
  entityName,
  onFieldsUpdated,
  onAttributeChange
}) => {
  const { t, i18n } = useTranslation();
  const { hasRole, user } = useAuth();
  const [showConfig, setShowConfig] = useState(false);
  const [editingField, setEditingField] = useState(null); // { fieldName, value }
  const [editingValue, setEditingValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const isThai = i18n?.language?.toLowerCase().startsWith('th') || false;

  const getFieldLabel = (field) => {
    if (!field) return '';
    if (isThai) {
      return field.displayNameTH || field.displayName || field.name;
    }
    return field.displayName || field.displayNameTH || field.name;
  };

  const handleStartEdit = (field) => {
    const currentValue = attributes?.[field.name];
    setEditingField(field.name);
    
    // Handle different types
    if (Array.isArray(currentValue)) {
      setEditingValue(JSON.stringify(currentValue, null, 2));
    } else if (typeof currentValue === 'object' && currentValue !== null) {
      setEditingValue(JSON.stringify(currentValue, null, 2));
    } else {
      setEditingValue(currentValue !== undefined && currentValue !== null ? String(currentValue) : '');
    }
  };

  const handleCancelEdit = () => {
    setEditingField(null);
    setEditingValue('');
  };

  const handleSaveEdit = async (field) => {
    if (!onAttributeChange) {
      console.warn('onAttributeChange callback not provided');
      return;
    }

    setIsSaving(true);
    try {
      let valueToSave = editingValue;

      // Parse based on field type
      const fieldType = field?.metadata?.attributeType || field?.ui?.inputType || field?.dataType || 'text';
      
      if (fieldType === 'number') {
        valueToSave = editingValue ? parseFloat(editingValue) : null;
      } else if (fieldType === 'date') {
        valueToSave = editingValue || null;
      } else if (fieldType === 'table') {
        try {
          valueToSave = JSON.parse(editingValue);
        } catch (e) {
          alert('Invalid JSON format for table data');
          setIsSaving(false);
          return;
        }
      }

      await onAttributeChange(field.name, valueToSave);
      setEditingField(null);
      setEditingValue('');
    } catch (error) {
      console.error('Error saving attribute:', error);
      alert('Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  // Check if user can configure - allow Admin or Manager roles
  // hasRole function checks user.roles array for RoleName property
  const hasAdminRole = hasRole('Admin');
  const hasManagerRole = hasRole('Manager');
  const hasAnyRole = hasRole(['Manager', 'Admin']);
  
  const canConfigure = (hasAdminRole || hasManagerRole || hasAnyRole) && entityName;
  
  // Debug logging to help troubleshoot role issues
  React.useEffect(() => {
    console.log('🔧 CustomAttributesDisplay - Role check debug:', {
      hasRoleManager: hasManagerRole,
      hasRoleAdmin: hasAdminRole,
      hasRoleArray: hasAnyRole,
      entityName,
      canConfigure,
      userRoles: user?.roles,
      roleNames: user?.roles?.map(r => r.RoleName || r.roleName || r),
      hasRoleFunction: typeof hasRole,
      userObject: user
    });
  }, [hasAdminRole, hasManagerRole, hasAnyRole, entityName, canConfigure, user, hasRole]);

  // Show component even if no fields if user can configure (so they can add fields)
  if (!fields.length && !canConfigure) {
    return null;
  }

  const hasValues = fields.some((field) => {
    const value = attributes?.[field.name];
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    return value !== undefined && value !== null && value !== '';
  });

  const renderTable = (rows, columns, totals) => (
    <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
        <thead className="bg-gray-50 dark:bg-gray-800">
          <tr>
            {columns.map((col, idx) => (
              <th
                key={col.key || idx}
                className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              >
                <div className="flex items-center space-x-1">
                  <span>{typeof col.label === 'string' ? col.label : String(col.label || '')}</span>
                  {col.type === 'calculated' && (
                    <Calculator className="h-3 w-3 text-blue-500" title="Calculated Column" />
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-800">
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {columns.map((col) => {
                // Calculate value for calculated columns
                let displayValue;
                if (col.type === 'calculated' && col.formula) {
                  const calculated = evaluateFormula(col.formula, row, columns);
                  if (calculated !== null && !isNaN(calculated)) {
                    // Format calculated values as numbers
                    const formatConfig = col.numberFormat || { preset: 'decimal2' };
                    displayValue = formatNumber(calculated, formatConfig);
                  } else {
                    displayValue = 'Error';
                  }
                } else if (col.type === 'number') {
                  // Format number columns
                  const value = row[col.key];
                  if (value !== undefined && value !== null && value !== '') {
                    const numValue = typeof value === 'number' ? value : parseFloat(value);
                    if (!isNaN(numValue)) {
                      const formatConfig = col.numberFormat || { preset: 'none' };
                      displayValue = formatNumber(numValue, formatConfig);
                    } else {
                      displayValue = String(value);
                    }
                  } else {
                    displayValue = '-';
                  }
                } else {
                  displayValue = typeof row[col.key] === 'object'
                    ? JSON.stringify(row[col.key])
                    : String(row[col.key] || '-');
                }

                return (
                  <td 
                    key={col.key} 
                    className={`px-3 py-2 whitespace-nowrap ${
                      col.type === 'calculated'
                        ? 'text-blue-600 dark:text-blue-400 font-medium'
                        : 'text-gray-900 dark:text-white'
                    }`}
                  >
                    {displayValue}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
        {/* Total Row */}
        {Object.keys(totals).length > 0 && (
          <tfoot className="bg-gray-100 dark:bg-gray-800 border-t-2 border-gray-300 dark:border-gray-600">
            <tr>
              {columns.map((col, idx) => (
                <td key={col.key} className="px-3 py-2 whitespace-nowrap">
                  {col.showTotal && totals[col.key] !== undefined ? (
                    <div className="text-sm font-bold text-gray-900 dark:text-white">
                      {typeof totals[col.key] === 'number' 
                        ? formatNumber(totals[col.key], col.numberFormat || { preset: 'decimal2' })
                        : totals[col.key]}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {idx === 0 ? 'Total:' : ''}
                    </div>
                  )}
                </td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );

  return (
    <>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900/20 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <FileText className="h-5 w-5 text-gray-400 dark:text-gray-500" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              {title}
            </h3>
          </div>
          {canConfigure && (
            <button
              onClick={() => setShowConfig(true)}
              className="p-2 text-gray-400 hover:text-teal-600 dark:hover:text-teal-400 transition-colors"
              title="Configure custom attributes"
            >
              <Settings className="h-5 w-5" />
            </button>
          )}
        </div>
      {fields.length === 0 && canConfigure ? (
        <div className="text-center py-8">
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            {t('customAttributes.noFields', 'No custom attribute fields configured yet.')}
          </p>
          <button
            onClick={() => setShowConfig(true)}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
          >
            <Settings className="h-4 w-4" />
            <span>Configure Custom Attributes</span>
          </button>
        </div>
      ) : hasValues ? (
        <div className="space-y-4">
          {fields.map((field) => {
            const rawValue = attributes?.[field.name];
            if (rawValue === undefined || rawValue === null || rawValue === '' || (Array.isArray(rawValue) && rawValue.length === 0)) {
              return null;
            }

            const attributeType =
              field?.metadata?.attributeType ||
              field?.ui?.inputType ||
              field?.dataType ||
              'text';

            const effectiveType = ['select', 'dropdown'].includes(attributeType)
              ? 'choice'
              : attributeType;

            let displayValue = formatAttributeValue(rawValue, field);

            if (effectiveType === 'choice') {
              const options = getChoiceOptions(field);
              const rawValuesArray = Array.isArray(rawValue) ? rawValue : [rawValue];
              const mappedValues = rawValuesArray
                .map((value) => {
                  const option = options.find((opt) => opt.value === value);
                  if (!option) return value;
                  if (isThai) {
                    return option.labelTH || option.label || option.value;
                  }
                  return option.label || option.labelTH || option.value;
                })
                .filter(Boolean);

              if (mappedValues.length > 0) {
                displayValue = Array.isArray(rawValue)
                  ? mappedValues.join(', ')
                  : mappedValues[0];
              }
            }

            // Special rendering for table type
            if (effectiveType === 'table' && Array.isArray(rawValue) && rawValue.length > 0) {
              const columns = field.columns || field.ui?.columns || [];
              // Calculate totals (called directly since we're already in render)
              const totals = calculateTotals(rawValue, columns, evaluateFormula);
              const isEditingTable = editingField === field.name;

              return (
                <div key={field.name}>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">
                      {getFieldLabel(field)}
                    </label>
                    {onAttributeChange && !isEditingTable && (
                      <button
                        onClick={() => handleStartEdit(field)}
                        className="p-1 text-gray-400 hover:text-teal-600 dark:hover:text-teal-400 transition-colors"
                        title="Edit table data"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  
                  {isEditingTable ? (
                    // Edit Mode for Table (JSON editor)
                    <div className="space-y-2">
                      {(() => {
                        let previewRows = null;
                        let previewError = null;
                        if (editingValue) {
                          try {
                            const parsed = JSON.parse(editingValue);
                            if (Array.isArray(parsed)) {
                              previewRows = parsed;
                            } else {
                              previewError = 'Preview unavailable: JSON must be an array of rows.';
                            }
                          } catch (error) {
                            previewError = 'Preview unavailable: invalid JSON.';
                          }
                        }
                        if (!previewRows || previewRows.length === 0) {
                          return previewError ? (
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {previewError}
                            </p>
                          ) : null;
                        }
                        const previewTotals = calculateTotals(previewRows, columns, evaluateFormula);
                        return (
                          <div className="space-y-1">
                            <span className="text-xs text-gray-500 dark:text-gray-400">Preview</span>
                            {renderTable(previewRows, columns, previewTotals)}
                          </div>
                        );
                      })()}
                      <textarea
                        value={editingValue}
                        onChange={(e) => setEditingValue(e.target.value)}
                        rows={10}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        placeholder="Enter table data as JSON array"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Edit the JSON data directly. Format: [{"{"}"column_key": "value"{"}"}]
                      </p>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleSaveEdit(field)}
                          disabled={isSaving}
                          className="inline-flex items-center space-x-1 px-3 py-1.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                        >
                          <Save className="h-3 w-3" />
                          <span>{isSaving ? 'Saving...' : 'Save'}</span>
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          disabled={isSaving}
                          className="inline-flex items-center space-x-1 px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 text-sm"
                        >
                          <X className="h-3 w-3" />
                          <span>Cancel</span>
                        </button>
                      </div>
                    </div>
                  ) : (
                    // Display Mode for Table
                    renderTable(rawValue, columns, totals)
                  )}
                </div>
              );
            }

            // Check if this field is being edited
            const isEditing = editingField === field.name;

            return (
              <div key={field.name}>
                <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                  {getFieldLabel(field)}
                </label>
                
                {isEditing ? (
                  // Edit Mode
                  <div className="space-y-2">
                    {effectiveType === 'choice' ? (
                      <select
                        value={editingValue}
                        onChange={(e) => setEditingValue(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      >
                        <option value="">-- Select --</option>
                        {getChoiceOptions(field).map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {isThai ? (opt.labelTH || opt.label) : opt.label}
                          </option>
                        ))}
                      </select>
                    ) : effectiveType === 'date' ? (
                      <input
                        type="date"
                        value={editingValue}
                        onChange={(e) => setEditingValue(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      />
                    ) : effectiveType === 'number' ? (
                      <div className="space-y-2">
                        <input
                          type="number"
                          step="any"
                          value={editingValue}
                          onChange={(e) => setEditingValue(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                          placeholder="Enter a number"
                        />
                        {/* Formatted preview - show how the number will be displayed */}
                        {editingValue && editingValue !== '' && editingValue !== '-' && (() => {
                          const numValue = parseFloat(editingValue);
                          if (!isNaN(numValue)) {
                            // Use the same formatting logic as display mode for consistency
                            const formattedDisplay = formatAttributeValue(numValue, field);
                            const rawValueStr = String(numValue);
                            
                            // Show preview if formatting was applied (formatted differs from raw)
                            if (formattedDisplay !== rawValueStr) {
                              return (
                                <div className="px-3 py-2 bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-lg">
                                  <div className="flex items-center space-x-2">
                                    <span className="text-xs text-teal-700 dark:text-teal-300 font-medium">Formatted preview:</span>
                                    <span className="text-sm text-teal-900 dark:text-teal-100 font-mono font-semibold">
                                      {formattedDisplay}
                                    </span>
                                  </div>
                                </div>
                              );
                            }
                          }
                          return null;
                        })()}
                      </div>
                    ) : (
                      <input
                        type="text"
                        value={editingValue}
                        onChange={(e) => setEditingValue(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      />
                    )}
                    
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleSaveEdit(field)}
                        disabled={isSaving}
                        className="inline-flex items-center space-x-1 px-3 py-1.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                      >
                        <Save className="h-3 w-3" />
                        <span>{isSaving ? 'Saving...' : 'Save'}</span>
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        disabled={isSaving}
                        className="inline-flex items-center space-x-1 px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 text-sm"
                      >
                        <X className="h-3 w-3" />
                        <span>Cancel</span>
                      </button>
                    </div>
                  </div>
                ) : (
                  // Display Mode
                  <div className="flex items-center justify-between group">
                    <p className="text-gray-900 dark:text-gray-100 flex-1">
                      {String(displayValue)}
                    </p>
                    {onAttributeChange && (
                      <button
                        onClick={() => handleStartEdit(field)}
                        className="ml-2 p-1 text-gray-400 hover:text-teal-600 dark:hover:text-teal-400 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Edit value"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('customAttributes.noData', 'No additional data captured yet.')}
        </p>
      )}
      </div>

      {/* Configuration Modal */}
      {showConfig && canConfigure && (
        <CustomAttributesConfig
          fields={fields}
          entityName={entityName}
          onFieldsUpdated={async () => {
            if (onFieldsUpdated) {
              await onFieldsUpdated();
            }
            setShowConfig(false);
          }}
          onClose={() => setShowConfig(false)}
        />
      )}
    </>
  );
};

export default CustomAttributesDisplay;

